import { PaginationParams } from '@/shared/api/pagination';
import { z } from 'zod/v4';
import { HITypeStatusDTO } from './hi-type';
import { TQLHILinkTypeDTO } from './hi-link';
import { SeverityDTO } from './severity';
// adjust import

/** HistoryInterval */
export const HistoryIntervalDTO = z.object({
  from: z.iso.datetime(),
  to: z.iso.datetime().optional(),
});
export type HistoryInterval = z.infer<typeof HistoryIntervalDTO>;

/** HIHistoryDTO */
export const HIHistoryDTO = z.object({
  hi_link_id: z.uuid(),
  hi_type_id: z.uuid(),
  data: z.array(HITypeStatusDTO),
});
export type HIHistory = z.infer<typeof HIHistoryDTO>;

/** HIHistoryCIExtendResponse */
export const HIHistoryCIExtendedDTO = z.object({
  ci_id: z.uuid(),
  data: z.array(HIHistoryDTO),
});
export type HIHistoryCIExtended = z.infer<typeof HIHistoryCIExtendedDTO>;

/** HIHistoryCIRequest */
export const HIHistoryCIRequestDTO = z.object({
  cis: z.array(z.any()).optional(),
  interval: HistoryIntervalDTO,
});
export type HIHistoryCIRequest = z.infer<typeof HIHistoryCIRequestDTO>;

/** HIHistoryCIResponse */
export const HIHistoryCIResponseDTO = z.object({
  ci_id: z.uuid(),
  data: z.array(HITypeStatusDTO),
});
export type HIHistoryCIResponse = z.infer<typeof HIHistoryCIResponseDTO>;

/** HIHistoryLevelDTO */
export const HIHistoryLevelDTO = z.object({
  hi_id: z.uuid(),
  ci_id: z.uuid(),
  ci_name: z.string(),
  ci_type_name: z.string(),
  sot_id: z.uuid(),
  severity: SeverityDTO,
  hi_type_name: z.string(),
  started_at: z.iso.datetime(),
  finished_at: z.iso.datetime(),
  duration: z.string(),
  type: TQLHILinkTypeDTO,
});
export type HIHistoryLevel = z.infer<typeof HIHistoryLevelDTO>;

export const HIHistoryLevelUpDTO = HIHistoryLevelDTO.extend({
  has_level_up: z.boolean(),
});
export type HIHistoryLevelUp = z.infer<typeof HIHistoryLevelUpDTO>;

export const HIHistoryLevelDownDTO = HIHistoryLevelDTO.extend({
  has_depends: z.boolean(),
});
export type HIHistoryLevelDown = z.infer<typeof HIHistoryLevelDownDTO>;

export const HIHistorySOTDTO = HIHistoryLevelDTO.extend({
  sot_id: z.uuid(),
});
export type HIHistorySOT = z.infer<typeof HIHistorySOTDTO>;

/** Annotation DTO */
export const HIAnnotationDTO = z
  .object({
    started_at: z.iso.datetime({ offset: true }).nullish(),
    description: z.string().nullish(),
    summary: z.string().nullish(),
    severity: SeverityDTO,
  })
  .catchall(z.union([z.string(), z.number()]));

export type HIAnnotation = z.infer<typeof HIAnnotationDTO>;

export const HIDataBaseDTO = z.object({
  hi_id: z.string(),
  sot_id: z.string().optional(),
  value: z.number(),
  hi_type_name: z.string(),
  type: TQLHILinkTypeDTO,
  has_depends: z.boolean(),
});

/** Dates DTO */
const HIHistoryHIValuesDatesDTO = z.object({
  started_at: z.iso.datetime({ offset: true }).optional(),
  finished_at: z.iso.datetime({ offset: true }).optional(),
});
type HIValuesDates = z.infer<typeof HIHistoryHIValuesDatesDTO>;

export type Params = HIValuesDates &
  PaginationParams & {
    sot_id?: string;
  };

export const HIHistoryHIValuesBaseDTO = z.object({
  hi_id: z.string(),
  tql_hi_link_id: z.string().optional(),
  sot_id: z.string().optional(),
  value: z.number(),
  hi_type_name: z.string(),
  type: TQLHILinkTypeDTO,
  has_depends: z.boolean().optional(),
  label: z.string().optional(),
});

export const HIHistoryHIValuesDTO = HIHistoryHIValuesBaseDTO.extend(
  HIHistoryHIValuesDatesDTO.shape,
);

export const HIHistoryValuesDTO = z.object({
  cis_group_id: z.string().optional(),
  cis_group_life_time: z.number().optional(),
  hi_values: z.array(
    z.object({
      ci_id: z.uuid(),
      status: z.array(HIHistoryHIValuesDTO),
    }),
  ),
});

export type HIValuesBase = z.infer<typeof HIHistoryHIValuesBaseDTO> &
  Record<string, string | number>;
export type HIHistoryHIValues = z.infer<typeof HIHistoryHIValuesDTO>;
export type HIHistoryValues = z.infer<typeof HIHistoryValuesDTO>;

/** HIDataAlertsResponse */
export const HIDataAlertDTO = z.object({
  starts_at: z.string(),
  ends_at: z.string().nullable(),
  labels: z.record(z.string(), z.string()),
  id: z.string(),
  fingerprint: z.string(),
  annotations: z.object({
    description: z.string(),
    summary: z.string(),
  }),
  value: z.number().int(),
  is_manual_closable: z.boolean(),
});
export type HIDataAlert = z.infer<typeof HIDataAlertDTO>;

/** HIHistoryCIStatuses */
export const HIHistoryCIStatusesDTO = z.object({
  cis_group_id: z.string().optional(),
  cis_group_life_time: z.number().optional(),
  ci_statuses: z.array(z.object({ ci_id: z.uuid(), value: z.number() })),
});
export type HIHistoryCIStatuses = z.infer<typeof HIHistoryCIStatusesDTO>;

/** HICIsHistory */
export const HICIsHistoryDTO = z.array(
  z.object({
    hi_id: z.uuid(),
    ci_id: z.uuid(),
    ci_name: z.string(),
    hi_type_label: z.string(),
    tql_hi_link_id: z.uuid(),
    hi_type_name: z.string(),
    type: TQLHILinkTypeDTO,
    assign_at: z.iso.datetime(),
    unassign_at: z.iso.datetime().nullable(),
    sot: z.array(
      z.object({
        sot_id: z.uuid(),
        severity: SeverityDTO.min(1),
        started_at: z.iso.datetime(),
        finished_at: z.iso.datetime().nullable(),
      }),
    ),
  }),
);
export type HICIsHistory = z.infer<typeof HICIsHistoryDTO>;

export const HIGroupHistoryHiTypeDTO = z.object({
  name: z.string(),
  label: z.string(),
});
export type HIGroupHistoryHiType = z.infer<typeof HIGroupHistoryHiTypeDTO>;

export const HIGroupHistoryHiTypesRequestDTO = z.object({
  ci_type_names: z.array(z.string()),
});
export type HIGroupHistoryHiTypesRequest = z.infer<
  typeof HIGroupHistoryHiTypesRequestDTO
>;

export const HIGroupHistoryHiTypesResponseDTO = z.record(
  z.string(),
  z.array(HIGroupHistoryHiTypeDTO),
);
export type HIGroupHistoryHiTypesResponse = z.infer<
  typeof HIGroupHistoryHiTypesResponseDTO
>;

export const HIGroupHistoryRequestDTO = z.object({
  ci_ids: z.array(z.string()),
  hi_type_name: z.string(),
  start: z.string(),
  end: z.string(),
  step: z.number(),
});
export type HIGroupHistoryRequest = z.infer<typeof HIGroupHistoryRequestDTO>;

export const HIGroupHistorySeverityCountsDTO = z.record(z.string(), z.number());
export type HIGroupHistorySeverityCounts = z.infer<
  typeof HIGroupHistorySeverityCountsDTO
>;

export const HIGroupHistoryPanelDTO = z.object({
  central_ci_id: z.string(),
  central_ci_name: z.string(),
  related_count: z.number(),
  center: z
    .object({
      tql_hi_link_id: z.string().optional(),
      sot_id: z.string().nullish(),
      severity: SeverityDTO,
      started_at: z.string().nullish(),
      duration: z.number().nullish(),
    })
    .nullish(),
  donut: z.object({
    legend: HIGroupHistorySeverityCountsDTO,
  }),
  barchart: z.array(
    z.object({
      t: z.string(),
      counts: HIGroupHistorySeverityCountsDTO,
    }),
  ),
});
export type HIGroupHistoryPanel = z.infer<typeof HIGroupHistoryPanelDTO>;

export const HIGroupHistoryResponseDTO = z.object({
  panels: z.array(HIGroupHistoryPanelDTO),
});
export type HIGroupHistoryResponse = z.infer<typeof HIGroupHistoryResponseDTO>;